import { Object3D } from "three";
import { GLTFLoader, DRACOLoader } from "three-stdlib";
import { AxiosError } from "axios";
import { logout, refreshAccessToken } from "@/shared/api/axios";
import { useEffect, useState } from "react";
import { appSession } from "@/shared/lib/storage";

const modelsCache = new Map<string, Object3D>();
const dracoLoader = new DRACOLoader();
const gltfLoader = new GLTFLoader();

dracoLoader.setDecoderPath("/draco/");
gltfLoader.setDRACOLoader(dracoLoader);

type UseGltfBinaryProps = {
  key: string;
  url: string;
  skip?: boolean;
  traverse: (child: Object3D) => void;
};

export function useGltfBinary({
  key,
  url,
  skip,
  traverse,
}: UseGltfBinaryProps) {
  const [object, setObject] = useState<Object3D | null>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "error" | null>(
    null
  );
  const [progress, setProgress] = useState<number | null>(null);

  const onStart = () => {
    setStatus("loading");
    setProgress(0);
  };

  const onProgress = (event: ProgressEvent) => {
    setProgress(event.loaded / event.total);
  };

  const onLoaded = () => {
    setStatus("loaded");
    setProgress(null);
  };

  const loadBinaryModel = async (url: string) => {
    if (modelsCache.has(key)) {
      setObject(modelsCache.get(key) as Object3D);
      return;
    }

    onStart();
    setAuthHeader();

    try {
      const gltf = await gltfLoader.loadAsync(url, onProgress);
      gltf.scene.traverse(traverse);
      gltf.scene.name = key;
      modelsCache.set(key, gltf.scene);
      setObject(gltf.scene);
      onLoaded();
    } catch (error) {
      setStatus("error");
      return handleError(error, url);
    }
  };

  const setAuthHeader = () => {
    const user = appSession.getItem();
    const token = user ? `Bearer ${user.access_token}` : "";

    gltfLoader.setRequestHeader({ Authorization: token });
  };

  const handleError = async (error: unknown, url: string): Promise<void> => {
    if ((error as AxiosError).response?.status === 401) {
      try {
        await refreshAccessToken();
        return loadBinaryModel(url);
      } catch (error) {
        logout();
        return;
      }
    }
  };

  useEffect(() => {
    if (skip) return;
    loadBinaryModel(url);
  }, [url, key, skip]);

  return [object, { status, progress }] as const;
}
